import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Trash2, Pencil, Check, X, GripVertical, WifiOff, ExternalLink } from 'lucide-react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import api from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
const METHODS = ['법인카드','세금계산서','현금영수증','영수증','개인카드'];
const fmt = n => (n || 0).toLocaleString('ko-KR');

const emptyCommon = () => ({
  name: '', description: '', unitPrice: 0, quantity: 1, price: 0,
  link: '', buyer: '', purchaseDate: '', method: '',
});

export default function EventInventoryTab({ eventId }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const me = JSON.parse(localStorage.getItem('paper_work_user') || '{}');
  const token = localStorage.getItem('paper_work_token');
  const isSuperAdmin = me?.role === 'superadmin';

  const [commonItems, setCommonItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editBuf, setEditBuf] = useState({});
  const [newItem, setNewItem] = useState(null);
  const [focusedCell, setFocusedCell] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // ─── Yjs ─────────────────────────────────────────────
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const versionRef = useRef(0);
  const peersRef = useRef('[]');

  const wsUrl = useMemo(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/yjs`;
  }, []);

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider(wsUrl, `event-common-${eventId}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/event-manage/projects/${eventId}/items-table`);
      setCommonItems(data.commonItems || []);
    } catch {}
    setLoading(false);
  }, [eventId]);

  useEffect(() => {
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    if (provider.wsconnected) setConnected(true);
    provider.awareness.setLocalStateField('user', {
      name: me?.name || me?.username || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const onAwareness = () => {
      const myName = me?.name || me?.username || 'Unknown';
      const seen = new Set(); const users = [];
      provider.awareness.getStates().forEach(state => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) { seen.add(state.user.name); users.push({ ...state.user, isMe: state.user.name === myName }); }
      });
      const pStr = JSON.stringify(users);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
    };
    provider.awareness.on('change', onAwareness);
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const notifyChange = () => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  };

  // ─── CRUD ─────────────────────────────────────────────
  const startEdit = (item) => {
    setEditingId(item.id);
    setEditBuf({ ...item });
  };
  const cancelEdit = () => { setEditingId(null); setEditBuf({}); };
  const saveEdit = async () => {
    if (!editBuf.name?.trim()) return;
    const { id, eventLayoutId, createdAt, updatedAt, order, ...fields } = editBuf;
    try {
      await api.put(`/event-manage/items-table/common/${editingId}`, fields);
      setCommonItems(p => p.map(it => it.id === editingId ? { ...it, ...fields } : it));
      setEditingId(null); setEditBuf({}); notifyChange();
    } catch { alert('저장 중 오류가 발생했습니다.'); }
  };
  const deleteCommon = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await api.delete(`/event-manage/items-table/common/${id}`);
      setCommonItems(p => p.filter(it => it.id !== id)); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };
  const addCommon = async () => {
    if (!newItem?.name?.trim()) return;
    try {
      const { data } = await api.post(`/event-manage/projects/${eventId}/items-table/common`, { ...newItem, order: commonItems.length });
      setCommonItems(p => [...p, data]);
      setNewItem(null); notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 드래그
  const onDragStart = (id) => setDragItem(id);
  const onDragOver  = (e, id) => { e.preventDefault(); setDragOverItem(id); };
  const onDrop = (targetId) => {
    if (!dragItem || dragItem === targetId) return;
    const arr = [...commonItems];
    const from = arr.findIndex(it => it.id === dragItem);
    const to   = arr.findIndex(it => it.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = arr.splice(from, 1); arr.splice(to, 0, moved);
    setCommonItems(arr);
    Promise.all(arr.map((it, i) => api.put(`/event-manage/items-table/common/${it.id}`, { order: i }))).then(() => notifyChange());
    setDragItem(null); setDragOverItem(null);
  };

  const totalPrice = commonItems.reduce((s, it) => s + (Number(it.price) || 0), 0);

  const cellCls = `border ${isDark ? 'border-white/10' : 'border-gray-200'} px-2 py-1.5 text-xs`;
  const inputCls = `w-full bg-transparent ${isDark ? 'text-white' : 'text-gray-900'} text-xs outline-none rounded px-1 py-0.5`;
  const numInputCls = `${inputCls} text-right`;
  const optBg = isDark ? '#1a1428' : '#fff';

  const ColHeader = ({ label, cls = '' }) => (
    <th className={`${cellCls} font-medium py-2 ${isDark ? 'bg-white/5 text-white/40' : 'bg-gray-50 text-gray-500'} ${cls}`}>{label}</th>
  );

  const EditInput = ({ field, type = 'text', className = '' }) => (
    <input type={type} value={editBuf[field] ?? ''}
      onChange={e => setEditBuf(p => {
        const v = type === 'number' ? (Number(e.target.value) || 0) : e.target.value;
        const u = { ...p, [field]: v };
        if (field === 'unitPrice' || field === 'quantity') u.price = (field === 'unitPrice' ? v : p.unitPrice || 0) * (field === 'quantity' ? v : p.quantity || 1);
        return u;
      })}
      className={`${type === 'number' ? numInputCls : inputCls} ${className}`} />
  );

  const NewInput = ({ field, type = 'text', placeholder = '', className = '' }) => (
    <input type={type} value={newItem?.[field] ?? ''} placeholder={placeholder}
      onChange={e => setNewItem(p => {
        const v = type === 'number' ? (Number(e.target.value) || 0) : e.target.value;
        const u = { ...p, [field]: v };
        if (field === 'unitPrice' || field === 'quantity') u.price = (field === 'unitPrice' ? v : p.unitPrice || 0) * (field === 'quantity' ? v : p.quantity || 1);
        return u;
      })}
      className={`${type === 'number' ? numInputCls : inputCls} ${className} ${isDark ? 'placeholder:text-white/20' : 'placeholder:text-gray-300'}`} />
  );

  if (loading) return <div className={`py-20 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>로딩 중...</div>;

  return (
    <div>
      {/* 상단 */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm">
          <span className={isDark ? 'text-white/40' : 'text-gray-400'}>총 지출</span>
          <span className={`font-semibold ml-2 text-amber-500`}>{fmt(totalPrice)}원</span>
          <span className={`ml-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>총 {commonItems.length}개 품목</span>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <button onClick={() => setNewItem(emptyCommon())}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-white/70' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
              <Plus size={12} /> 항목 추가
            </button>
          )}
          <div className="flex items-center gap-1.5">
            {peers.slice(0, 5).map((p, i) => (
              <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 ${p.isMe ? 'border-brand' : 'border-transparent'}`}
                style={{ backgroundColor: p.color }} title={p.name}>{p.name?.charAt(0)}</div>
            ))}
            {!connected && <WifiOff size={13} className="text-red-400" />}
          </div>
        </div>
      </div>

      <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent">
          <table className="text-xs border-collapse min-w-full">
            <thead>
              <tr className="text-center">
                <ColHeader label="" cls="w-7" />
                <ColHeader label="#" cls="w-8" />
                <ColHeader label="품목명" cls="min-w-[130px] text-left" />
                <ColHeader label="설명" cls="min-w-[160px] text-left" />
                <ColHeader label="단가" cls="w-24 text-right" />
                <ColHeader label="수량" cls="w-14 text-center" />
                <ColHeader label="금액" cls="w-24 text-right" />
                <ColHeader label="구매링크" cls="w-20 text-center" />
                <ColHeader label="구매자" cls="w-20 text-center" />
                <ColHeader label="구매일자" cls="w-[88px] text-center" />
                <ColHeader label="결제방법" cls="w-24 text-center" />
                <ColHeader label="" cls="w-16" />
              </tr>
            </thead>
            <tbody>
              {commonItems.length === 0 && !newItem && (
                <tr><td colSpan={12} className={`py-10 text-center ${isDark ? 'text-white/30' : 'text-gray-400'}`}>공통물품이 없습니다. "항목 추가"를 눌러 추가하세요.</td></tr>
              )}
              {commonItems.map((it, idx) => {
                const isEditing = editingId === it.id;
                const isDragging = dragItem === it.id;
                const isDragOver = dragOverItem === it.id;
                const rowBg = it.purchaseDate
                  ? (isDark ? 'bg-emerald-500/5' : 'bg-emerald-50/50')
                  : (isDark ? 'hover:bg-white/3' : 'hover:bg-gray-50');
                return (
                  <tr key={it.id} draggable={isSuperAdmin && !isEditing}
                    onDragStart={isSuperAdmin && !isEditing ? () => onDragStart(it.id) : undefined}
                    onDragOver={isSuperAdmin ? e => onDragOver(e, it.id) : undefined}
                    onDrop={isSuperAdmin ? () => onDrop(it.id) : undefined}
                    onDragEnd={() => { setDragItem(null); setDragOverItem(null); }}
                    className={`transition-colors ${rowBg} ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-t-2 border-brand' : ''}`}>
                    <td className={`${cellCls} text-center`}>
                      {isSuperAdmin && <GripVertical size={11} className={`cursor-grab mx-auto ${isDark ? 'text-white/15' : 'text-gray-300'}`} />}
                    </td>
                    <td className={`${cellCls} text-center ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{idx + 1}</td>
                    <td className={cellCls}>
                      {isEditing
                        ? <EditInput field="name" />
                        : <span className={`px-1 ${isDark ? 'text-white' : 'text-gray-800'} font-medium`}>{it.name}</span>}
                    </td>
                    <td className={cellCls}>
                      {isEditing
                        ? <EditInput field="description" placeholder="설명" />
                        : <span className={`px-1 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{it.description}</span>}
                    </td>
                    <td className={cellCls}>
                      {isEditing
                        ? <EditInput field="unitPrice" type="number" />
                        : <span className={`text-right block px-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>{fmt(it.unitPrice)}</span>}
                    </td>
                    <td className={cellCls}>
                      {isEditing
                        ? <EditInput field="quantity" type="number" className="text-center" />
                        : <span className={`text-center block px-1 ${isDark ? 'text-white' : 'text-gray-700'}`}>{it.quantity || 1}</span>}
                    </td>
                    <td className={cellCls}>
                      {isEditing
                        ? <span className={`text-right block px-1 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt((editBuf.unitPrice || 0) * (editBuf.quantity || 1))}</span>
                        : <span className={`text-right block px-1 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(it.price || it.unitPrice * (it.quantity || 1))}</span>}
                    </td>
                    <td className={`${cellCls} text-center`}>
                      {isEditing
                        ? <EditInput field="link" placeholder="https://..." />
                        : it.link
                          ? <a href={it.link} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-0.5 text-brand hover:underline"><ExternalLink size={11} />링크</a>
                          : <span className={isDark ? 'text-white/20' : 'text-gray-300'}>-</span>}
                    </td>
                    <td className={`${cellCls} text-center`}>
                      {isEditing
                        ? <EditInput field="buyer" className="text-center" />
                        : <span className={`${isDark ? 'text-white' : 'text-gray-700'}`}>{it.buyer || '-'}</span>}
                    </td>
                    <td className={`${cellCls} text-center`}>
                      {isEditing
                        ? <input type="date" value={editBuf.purchaseDate || ''}
                            onChange={e => setEditBuf(p => ({ ...p, purchaseDate: e.target.value }))}
                            className={`w-full bg-transparent text-[11px] outline-none rounded px-0.5 py-0.5 text-center ${isDark ? 'text-white [color-scheme:dark]' : 'text-gray-900 [color-scheme:light]'}`} />
                        : <span className={`${isDark ? 'text-white' : 'text-gray-700'}`}>{it.purchaseDate || '-'}</span>}
                    </td>
                    <td className={`${cellCls} text-center`}>
                      {isEditing
                        ? <select value={editBuf.method || ''} onChange={e => setEditBuf(p => ({ ...p, method: e.target.value }))}
                            className={`w-full bg-transparent text-xs outline-none rounded px-1 py-0.5 cursor-pointer ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <option value="" style={{ background: optBg }}>-</option>
                            {METHODS.map(m => <option key={m} value={m} style={{ background: optBg }}>{m}</option>)}
                          </select>
                        : <span className={`${isDark ? 'text-white' : 'text-gray-700'}`}>{it.method || '-'}</span>}
                    </td>
                    <td className={`${cellCls} text-center`}>
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={saveEdit} className="text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                          <button onClick={cancelEdit} className={isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-300 hover:text-gray-500'}><X size={13} /></button>
                        </div>
                      ) : isSuperAdmin ? (
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => startEdit(it)} className={`transition-colors ${isDark ? 'text-white/20 hover:text-white/60' : 'text-gray-300 hover:text-gray-600'}`}><Pencil size={12} /></button>
                          <button onClick={() => deleteCommon(it.id)} className={`transition-colors ${isDark ? 'text-white/20 hover:text-red-400' : 'text-gray-300 hover:text-red-500'}`}><Trash2 size={12} /></button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}

              {/* 새 항목 입력 행 */}
              {newItem && (
                <tr className={isDark ? 'bg-brand/5 border-t border-brand/20' : 'bg-brand/5 border-t border-brand/20'}>
                  <td className={cellCls}></td>
                  <td className={`${cellCls} text-center ${isDark ? 'text-white/30' : 'text-gray-400'}`}>+</td>
                  <td className={cellCls}>
                    <NewInput field="name" placeholder="품목명*" />
                  </td>
                  <td className={cellCls}>
                    <NewInput field="description" placeholder="설명" />
                  </td>
                  <td className={cellCls}>
                    <NewInput field="unitPrice" type="number" placeholder="0" />
                  </td>
                  <td className={cellCls}>
                    <NewInput field="quantity" type="number" placeholder="1" />
                  </td>
                  <td className={cellCls}>
                    <span className={`text-right block px-1 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {fmt((newItem.unitPrice || 0) * (newItem.quantity || 1))}
                    </span>
                  </td>
                  <td className={cellCls}>
                    <NewInput field="link" placeholder="https://..." />
                  </td>
                  <td className={cellCls}>
                    <NewInput field="buyer" placeholder="구매자" />
                  </td>
                  <td className={cellCls}>
                    <input type="date" value={newItem.purchaseDate || ''}
                      onChange={e => setNewItem(p => ({ ...p, purchaseDate: e.target.value }))}
                      className={`w-full bg-transparent text-[11px] outline-none rounded px-0.5 py-0.5 text-center ${isDark ? 'text-white [color-scheme:dark]' : 'text-gray-900 [color-scheme:light]'}`} />
                  </td>
                  <td className={cellCls}>
                    <select value={newItem.method || ''} onChange={e => setNewItem(p => ({ ...p, method: e.target.value }))}
                      className={`w-full bg-transparent text-xs outline-none rounded px-1 py-0.5 cursor-pointer ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <option value="" style={{ background: optBg }}>-</option>
                      {METHODS.map(m => <option key={m} value={m} style={{ background: optBg }}>{m}</option>)}
                    </select>
                  </td>
                  <td className={`${cellCls} text-center`}>
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={addCommon} className="text-emerald-400 hover:text-emerald-300"><Check size={13} /></button>
                      <button onClick={() => setNewItem(null)} className={isDark ? 'text-white/30 hover:text-white/60' : 'text-gray-300 hover:text-gray-500'}><X size={13} /></button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {commonItems.length > 0 && (
              <tfoot>
                <tr className={isDark ? 'bg-white/5 text-white/60' : 'bg-gray-50 text-gray-500'}>
                  <td colSpan={6} className={`${cellCls} text-right font-medium`}>합계</td>
                  <td className={`${cellCls} text-right font-bold text-amber-500`}>{fmt(totalPrice)}</td>
                  <td colSpan={5} className={cellCls}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
